import React from 'react';
import ChineseEssayWritingToolboxPart2 from './ChineseEssayWritingToolboxPart2';
import { useState, useEffect } from 'react';
import { CheckSquare, Square, FileText, Users, Target, Edit3, List, MessageCircle, Eye, Wand2, Download, Clock, Globe, Sparkles, AlertCircle, CheckCircle } from 'lucide-react';
import deepSeekEssayService from '../services/deepseekEssayApi';
import { getWritingToolboxData, getCategoryById, getTemplateById, getLanguageTools } from '../data/writingToolboxData';
import html2pdf from 'html2pdf.js';

function ChineseEssayWritingToolbox() {
  // Core state management
  const [language, setLanguage] = useState('traditional');
  const [selectedType, setSelectedType] = useState('');
  const [selectedAudience, setSelectedAudience] = useState('');
  const [selectedTone, setSelectedTone] = useState('');
  const [mainTask, setMainTask] = useState('');
  const [requiredElements, setRequiredElements] = useState([]);
  const [contentPlanner, setContentPlanner] = useState({
    background: false,
    introduceTopic: false,
    stateMainIdea: false,
    focusedParagraphs: false,
    explanation: false,
    example: false,
    evidence: false,
    quote: false,
    counterarguments: false,
    summarize: false,
    restateOpinion: false,
    forwardLooking: false
  });
  const [supportingDetails, setSupportingDetails] = useState([]);
  const [promptBuilder, setPromptBuilder] = useState({
    type: '',
    audience: '',
    topic: '',
    task: '',
    keyPoints: '',
    tone: '',
    length: ''
  });
  const [selfCheck, setSelfCheck] = useState([]);
  const [activeTab, setActiveTab] = useState('type');
  const [generationType, setGenerationType] = useState('prompt');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  // Get data from the mock data file
  const toolboxData = getWritingToolboxData();
  const languageTools = getLanguageTools();

  // Text content for both languages
  const texts = {
    traditional: {
      title: '📝 中文寫作工具箱',
      subtitle: '全面的AI驅動工具，引導您從提示到完善的文章',
      essayTypes: toolboxData.categories.essayTypes.items.map(item => item.name),
      audiences: toolboxData.categories.audiences.items.map(item => item.name),
      tones: toolboxData.categories.tones.items.map(item => item.name),
      requiredElements: toolboxData.categories.requiredElements.items.map(item => item.name),
      supportingDetails: toolboxData.categories.supportingDetails.items.map(item => item.name),
      selfCheck: toolboxData.categories.selfCheckItems.items.map(item => item.name)
    },
    simplified: {
      title: '📝 中文写作工具箱',
      subtitle: '全面的AI驱动工具，引导您从提示到完善的文章',
      essayTypes: toolboxData.categories.essayTypes.items.map(item => item.name),
      audiences: toolboxData.categories.audiences.items.map(item => item.name),
      tones: toolboxData.categories.tones.items.map(item => item.name),
      requiredElements: toolboxData.categories.requiredElements.items.map(item => item.name),
      supportingDetails: toolboxData.categories.supportingDetails.items.map(item => item.name),
      selfCheck: toolboxData.categories.selfCheckItems.items.map(item => item.name)
    }
  };

  const t = texts[language];

  // Auto-fill prompt builder when parameters change
  useEffect(() => {
    setPromptBuilder(prev => ({
      ...prev,
      type: selectedType,
      audience: selectedAudience,
      tone: selectedTone,
      task: mainTask,
      keyPoints: requiredElements.join(language === 'traditional' ? '、' : '、')
    }));
  }, [selectedType, selectedAudience, selectedTone, mainTask, requiredElements, language]);

  const handleCheckboxChange = (item, list, setList) => {
    if (list.includes(item)) {
      setList(list.filter(i => i !== item));
    } else {
      setList([...list, item]);
    }
  };

  const handleContentPlannerChange = (key) => {
    setContentPlanner(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Parse length requirement to get target word count
  const parseLength = () => {
    const lengthStr = promptBuilder.length.toLowerCase();
    if (!lengthStr) return 500;
    
    const wordMatch = lengthStr.match(/(\d+)\s*[字詞]/);
    if (wordMatch) return parseInt(wordMatch[1]);
    
    const pageMatch = lengthStr.match(/(\d+)\s*[頁页]/);
    if (pageMatch) return parseInt(pageMatch[1]) * 250;
    
    const rangeMatch = lengthStr.match(/(\d+)\s*-\s*(\d+)\s*[字詞]/);
    if (rangeMatch) {
      const min = parseInt(rangeMatch[1]);
      const max = parseInt(rangeMatch[2]);
      return Math.floor((min + max) / 2);
    }
    
    if (lengthStr.includes('短') || lengthStr.includes('简')) return 300;
    if (lengthStr.includes('簡') || lengthStr.includes('简要')) return 250;
    if (lengthStr.includes('長') || lengthStr.includes('长') || lengthStr.includes('詳') || lengthStr.includes('详')) return 800;
    if (lengthStr.includes('段')) return 150;
    
    return 500;
  };

  // Generate prompt for DeepSeek API
  const generatePrompt = () => {
    const { type, audience, topic, task, keyPoints, tone, length } = promptBuilder;
    if (language === 'traditional') {
      return `為${audience || '[受眾]'}撰寫一篇關於${topic || '[主題]'}的${type || '[類型]'}。${task || '[任務]'}。包含${keyPoints || '[重點]'}。採用${tone || '[語調]'}語調。${length || '[長度要求]'}。`;
    } else {
      return `为${audience || '[受众]'}撰写一篇关于${topic || '[主题]'}的${type || '[类型]'}。${task || '[任务]'}。包含${keyPoints || '[重点]'}。采用${tone || '[语调]'}语调。${length || '[长度要求]'}。`;
    }
  };

  // Call DeepSeek API for essay generation
  const callDeepSeekAPI = async (prompt, options = {}) => {
    setIsGenerating(true);
    setApiError(null);
    setApiResponse(null);

    try {
      const response = await deepSeekEssayService.generateEssay(prompt, {
        language: language,
        essayType: options.essayType || 'general',
        targetLength: parseLength(),
        temperature: 0.7,
        maxTokens: 2000,
        ...options
      });

      const parsedResponse = deepSeekEssayService.parseResponse(response);
      console.log('API Response Debug:', {
        parsedResponse: parsedResponse,
        success: parsedResponse.success,
        content: parsedResponse.content
      });
      
      if (parsedResponse.success) {
        setApiResponse(parsedResponse);
        setGeneratedContent(parsedResponse.content);
        console.log('Content set successfully:', parsedResponse.content);
      } else {
        setApiError(parsedResponse.error);
        console.log('API Error:', parsedResponse.error);
      }
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle generation based on type
  const handleGenerate = async () => {
    const prompt = generatePrompt();
    console.log('Handle Generate Debug:', {
      generationType: generationType,
      prompt: prompt,
      promptBuilder: promptBuilder
    });
    
    switch (generationType) {
      case 'prompt':
        console.log('Setting prompt content:', prompt);
        setGeneratedContent(prompt);
        break;
      case 'template':
        const template = generateTemplate();
        console.log('Setting template content:', template);
        setGeneratedContent(template);
        break;
      case 'essay':
        console.log('Calling DeepSeek API for essay generation');
        await callDeepSeekAPI(prompt, {
          essayType: 'argumentative' // Default to argumentative for full essays
        });
        break;
    }
  };

  // Generate template (local generation)
  const generateTemplate = () => {
    const { type, topic } = promptBuilder;
    const targetWords = parseLength();
    const introWords = Math.floor(targetWords * 0.15);
    const bodyWords = Math.floor(targetWords * 0.7);
    const conclusionWords = Math.floor(targetWords * 0.15);
    
    let template = '';
    if (language === 'traditional') {
      template = `${type || '文章'}模板：${topic || '[您的主題]'}\n目標長度：${promptBuilder.length || '如指定'}（≈${targetWords}字）\n\n`;
      template += `開頭（≈${introWords}字）：\n═══════════════════════════════════════\n`;
      if (contentPlanner.background) template += `• 背景/情境：[提供相關背景信息]\n`;
      if (contentPlanner.introduceTopic) template += `• 主題介紹：[介紹主要主題]\n`;
      if (contentPlanner.stateMainIdea) template += `• 論點陳述：[陳述主要觀點]\n\n`;
      
      template += `正文段落（總共≈${bodyWords}字）：\n═══════════════════════════════════════\n`;
      requiredElements.forEach((element, index) => {
        template += `段落${index + 1}：${element}\n`;
        if (contentPlanner.explanation) template += `• 解釋：[詳細說明]\n`;
        if (contentPlanner.example) template += `• 例子：[提供具體例子]\n`;
        template += `\n`;
      });
      
      template += `結論（≈${conclusionWords}字）：\n═══════════════════════════════════════\n`;
      if (contentPlanner.summarize) template += `• 總結：[總結主要觀點]\n`;
      if (contentPlanner.restateOpinion) template += `• 重申：[重申立場]\n`;
      if (contentPlanner.forwardLooking) template += `• 前瞻：[未來展望]\n`;
    } else {
      template = `${type || '文章'}模板：${topic || '[您的主题]'}\n目标长度：${promptBuilder.length || '如指定'}（≈${targetWords}字）\n\n`;
      template += `开头（≈${introWords}字）：\n═══════════════════════════════════════\n`;
      if (contentPlanner.background) template += `• 背景/情境：[提供相关背景信息]\n`;
      if (contentPlanner.introduceTopic) template += `• 主题介绍：[介绍主要主题]\n`;
      if (contentPlanner.stateMainIdea) template += `• 论点陈述：[陈述主要观点]\n\n`;
      
      template += `正文段落（总共≈${bodyWords}字）：\n═══════════════════════════════════════\n`;
      requiredElements.forEach((element, index) => {
        template += `段落${index + 1}：${element}\n`;
        if (contentPlanner.explanation) template += `• 解释：[详细说明]\n`;
        if (contentPlanner.example) template += `• 例子：[提供具体例子]\n`;
        template += `\n`;
      });
      
      template += `结论（≈${conclusionWords}字）：\n═══════════════════════════════════════\n`;
      if (contentPlanner.summarize) template += `• 总结：[总结主要观点]\n`;
      if (contentPlanner.restateOpinion) template += `• 重申：[重申立场]\n`;
      if (contentPlanner.forwardLooking) template += `• 前瞻：[未来展望]\n`;
    }
    
    return template;
  };

  // Generate PDF from generated content
  const generatePDF = async () => {
    // If no content, try to generate a template as fallback
    let contentToUse = generatedContent;
    
    console.log('PDF Generation Debug:', {
      generatedContent: generatedContent,
      contentLength: generatedContent ? generatedContent.length : 0,
      promptBuilder: promptBuilder,
      generationType: generationType,
      contentToUse: contentToUse
    });
    if (!contentToUse || contentToUse.trim() === '') {
      console.log('No content available, generating fallback template');
      contentToUse = generateTemplate();
      if (!contentToUse || contentToUse.trim() === '') {
        // Final fallback - create a simple test content
        contentToUse = language === 'traditional' 
          ? '這是一個測試內容。請先生成一些內容再下載PDF。\n\n您可以選擇不同的生成類型：\n• 提示：生成寫作提示\n• 模板：生成文章模板\n• 文章：生成完整文章'
          : '这是一个测试内容。请先生成一些内容再下载PDF。\n\n您可以选择不同的生成类型：\n• 提示：生成写作提示\n• 模板：生成文章模板\n• 文章：生成完整文章';
        console.log('Using fallback test content:', contentToUse);
      }
    }

    setIsPdfGenerating(true);

    try {
      // Create a temporary container for PDF generation
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.height = 'auto';
      tempContainer.style.minHeight = '297mm'; // A4 height
      tempContainer.style.padding = '20mm';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.fontFamily = 'SimSun, Microsoft YaHei, serif';
      tempContainer.style.fontSize = '12pt';
      tempContainer.style.lineHeight = '1.6';
      tempContainer.style.color = '#333';
      tempContainer.style.overflow = 'visible';
      tempContainer.style.display = 'block';

      // Create PDF content with professional formatting
      const pdfContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${promptBuilder.topic || (language === 'traditional' ? '中文作文' : '中文作文')}</title>
          <style>
            body {
              font-family: 'SimSun', 'Microsoft YaHei', serif;
              font-size: 12pt;
              line-height: 1.6;
              color: #333;
              margin: 0;
              padding: 20mm;
              background-color: white;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .title {
              font-size: 18pt;
              font-weight: bold;
              margin-bottom: 10px;
              color: #2c3e50;
            }
            .metadata {
              font-size: 10pt;
              color: #7f8c8d;
              margin-bottom: 20px;
            }
            .divider {
              border-top: 2px solid #3498db;
              margin-bottom: 20px;
            }
            .content {
              white-space: pre-wrap;
              text-align: justify;
              font-size: 12pt;
              line-height: 1.6;
              margin-bottom: 20px;
            }
            .footer {
              border-top: 2px solid #3498db;
              margin-top: 30px;
              padding-top: 20px;
              text-align: center;
              font-size: 10pt;
              color: #7f8c8d;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${promptBuilder.topic || (language === 'traditional' ? '中文作文' : '中文作文')}</div>
            <div class="metadata">
              ${language === 'traditional' ? '生成時間：' : '生成时间：'}${new Date().toLocaleString()}
            </div>
            <div class="metadata">
              ${language === 'traditional' ? '類型：' : '类型：'}${promptBuilder.type || '未指定'} | 
              ${language === 'traditional' ? '受眾：' : '受众：'}${promptBuilder.audience || '未指定'} | 
              ${language === 'traditional' ? '語調：' : '语调：'}${promptBuilder.tone || '未指定'}
            </div>
          </div>
          
          <div class="divider"></div>
          
          <div class="content">${contentToUse.replace(/\n/g, '<br>')}</div>
          
          <div class="footer">
            ${language === 'traditional' ? '由中文寫作工具箱生成' : '由中文写作工具箱生成'} | 
            ${language === 'traditional' ? '字數：' : '字数：'}${contentToUse.replace(/\s/g, '').length}字
          </div>
        </body>
        </html>
      `;

      console.log('PDF Content to be generated:', pdfContent);
      tempContainer.innerHTML = pdfContent;
      document.body.appendChild(tempContainer);

      // Configure PDF options
      const pdfOptions = {
        margin: [10, 10, 10, 10],
        filename: `${promptBuilder.topic || 'essay'}_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        }
      };

      console.log('PDF Options:', pdfOptions);
      console.log('Temp container content:', tempContainer.innerHTML);
      console.log('Temp container visible:', tempContainer.offsetWidth, tempContainer.offsetHeight);

      // Add to DOM and wait for rendering
      document.body.appendChild(tempContainer);
      
      // Wait a bit for the content to render properly
      await new Promise(resolve => setTimeout(resolve, 100));

      // Generate PDF
      try {
        await html2pdf().from(tempContainer).set(pdfOptions).save();
        console.log('PDF generated successfully');
      } catch (error) {
        console.error('HTML2PDF failed, trying alternative method:', error);
        
        // Alternative method: Create a simple text-based PDF
        const simpleContent = `
          ${promptBuilder.topic || (language === 'traditional' ? '中文作文' : '中文作文')}
          
          ${language === 'traditional' ? '生成時間：' : '生成时间：'}${new Date().toLocaleString()}
          ${language === 'traditional' ? '類型：' : '类型：'}${promptBuilder.type || '未指定'}
          ${language === 'traditional' ? '受眾：' : '受众：'}${promptBuilder.audience || '未指定'}
          ${language === 'traditional' ? '語調：' : '语调：'}${promptBuilder.tone || '未指定'}
          
          ========================================
          
          ${contentToUse}
          
          ========================================
          
          ${language === 'traditional' ? '由中文寫作工具箱生成' : '由中文写作工具箱生成'} | 
          ${language === 'traditional' ? '字數：' : '字数：'}${contentToUse.replace(/\s/g, '').length}字
        `;
        
        // Create a blob and download
        const blob = new Blob([simpleContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${promptBuilder.topic || 'essay'}_${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        alert(language === 'traditional' ? 'PDF生成失敗，已下載文本文件作為替代' : 'PDF生成失败，已下载文本文件作为替代');
      }

      // Clean up
      document.body.removeChild(tempContainer);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert(language === 'traditional' ? 'PDF生成失敗，請重試' : 'PDF生成失败，请重试');
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const CheckboxItem = ({ checked, onChange, children }) => (
    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer" onClick={onChange}>
      {checked ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
      <span className={checked ? 'text-gray-900' : 'text-gray-600'}>{children}</span>
    </div>
  );

  const tabConfigs = [
    { id: 'type', label: language === 'traditional' ? '文章類型' : '文章类型', icon: FileText },
    { id: 'audience', label: language === 'traditional' ? '受眾與語調' : '受众与语调', icon: Users },
    { id: 'prompt', label: language === 'traditional' ? '提示分析' : '提示分析', icon: Target },
    { id: 'content', label: language === 'traditional' ? '內容規劃' : '内容规划', icon: Edit3 },
    { id: 'details', label: language === 'traditional' ? '支撐細節' : '支撑细节', icon: List },
    { id: 'language', label: language === 'traditional' ? '語言工具' : '语言工具', icon: MessageCircle },
    { id: 'builder', label: 'AI生成器', icon: Wand2 },
    { id: 'check', label: language === 'traditional' ? '自我檢查' : '自我检查', icon: Eye }
  ];

  // Pass all the data to Part 2 for rendering
  const part2Props = {
    language,
    setLanguage,
    selectedType,
    setSelectedType,
    selectedAudience,
    setSelectedAudience,
    selectedTone,
    setSelectedTone,
    mainTask,
    setMainTask,
    requiredElements,
    setRequiredElements,
    contentPlanner,
    setContentPlanner,
    supportingDetails,
    setSupportingDetails,
    promptBuilder,
    setPromptBuilder,
    selfCheck,
    setSelfCheck,
    activeTab,
    setActiveTab,
    generationType,
    setGenerationType,
    generatedContent,
    setGeneratedContent,
    isGenerating,
    setIsGenerating,
    apiResponse,
    setApiResponse,
    apiError,
    setApiError,
    isPdfGenerating,
    setIsPdfGenerating,
    generatePDF,
    texts,
    t,
    handleCheckboxChange,
    handleContentPlannerChange,
    parseLength,
    generatePrompt,
    callDeepSeekAPI,
    handleGenerate,
    generateTemplate,
    CheckboxItem,
    tabConfigs
  };

  return <ChineseEssayWritingToolboxPart2 {...part2Props} />;
}

export default ChineseEssayWritingToolbox;